import { memo } from 'react';
import { Search, X, Calendar } from 'lucide-react';

interface FilterBarProps {
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  statusValue?: string;
  onStatusChange?: (value: string) => void;
  statusOptions?: Array<{ value: string; label: string }>;
  dateFromValue?: string;
  onDateFromChange?: (value: string) => void;
  dateToValue?: string;
  onDateToChange?: (value: string) => void;
  extra?: React.ReactNode;
  onClear?: () => void;
}

export const FilterBar = memo(function FilterBar({
  searchPlaceholder = '搜尋...',
  searchValue,
  onSearchChange,
  statusValue,
  onStatusChange,
  statusOptions,
  dateFromValue,
  onDateFromChange,
  dateToValue,
  onDateToChange,
  extra,
  onClear,
}: FilterBarProps) {
  const hasFilters =
    searchValue !== '' ||
    (statusValue !== undefined && statusValue !== '') ||
    (dateFromValue !== undefined && dateFromValue !== '') ||
    (dateToValue !== undefined && dateToValue !== '');

  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-5">
      {/* 搜尋框 */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-brand-bake bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
        />
        {searchValue && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded"
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* 狀態下拉 */}
      {statusOptions && onStatusChange && (
        <select
          value={statusValue ?? ''}
          onChange={(e) => onStatusChange(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-brand-bake bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent min-w-[140px]"
        >
          <option value="">全部狀態</option>
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {/* 日期區間 */}
      {onDateFromChange && (
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="date"
            value={dateFromValue ?? ''}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="pl-10 pr-3 py-2.5 rounded-lg border border-brand-bake bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent w-[160px]"
            placeholder="起始日期"
          />
        </div>
      )}
      {onDateToChange && (
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="date"
            value={dateToValue ?? ''}
            onChange={(e) => onDateToChange(e.target.value)}
            className="pl-10 pr-3 py-2.5 rounded-lg border border-brand-bake bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent w-[160px]"
            placeholder="結束日期"
          />
        </div>
      )}

      {/* 清除篩選 */}
      {hasFilters && onClear && (
        <button
          onClick={onClear}
          className="inline-flex items-center gap-1 px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50"
        >
          <X className="h-4 w-4" />
          清除篩選
        </button>
      )}

      {/* 額外按鈕 */}
      {extra}
    </div>
  );
});
